import { Maybe } from '@investigator/shared/generated/graphql';
import { DetectionAlertItem } from '@investigator/features/entities/context';
import moment from 'moment';

export type BuildSearchQueryArgs = {
  alertType?: string;
  item: DetectionAlertItem;
  logScaleQuery?: string;
  start?: number;
  end?: number;
};

const encode = encodeURIComponent;

export function buildSearchQueryString({
  alertType,
  item,
  logScaleQuery,
  start,
  end,
}: BuildSearchQueryArgs): string {
  const formattedTime = moment.unix(item.alert_timestamp?.observed).format();
  const startTime = start ?? moment(formattedTime).subtract(1, 'hours').unix();
  const endTime = end ?? moment(formattedTime).add(24, 'hours').unix();
  const humioStart = startTime * 1000;
  const humioEnd = endTime * 1000;

  const base = logScaleQuery
    ? encode(logScaleQuery)
    : buildQueryByType(alertType ?? item?.alert_info?.alert_type, item);

  const params = `?query=${base}
&live=false
&humioStart=${humioStart}
&humioEnd=${humioEnd}
&fullscreen=false
&widgetType=table-view
&newestAtBottom=true
&showOnlyFirstLine=false
&start=${startTime}
&end=${endTime}
&configured-columns.uid.render-as=gotolink`;
  return params;
}

function buildQueryByType(
  type: Maybe<string> | undefined,
  item: DetectionAlertItem,
): string {
  const uid = ('uid' in item ? item.uid : '') ?? '';
  const entityName = item?.alert_entity?.entity_name ?? '';
  const alertName = item?.alert_info?.alert_name ?? '';
  const alertType = item?.alert_info?.alert_type ?? '';

  switch (type) {
    case 'suricata_corelight': {
      return `uid%3D${encode(uid)}%20OR%20(%23path%3Dsuricata_corelight%20AND%20id.orig_h%3D${encode(
        entityName,
      )}%20AND%20alert.signature%20%3D%20%22${encode(alertName)}%22%29%0A%7C%20groupby%28%5Buid%2C%20%23path%2C%20id.orig_h%2C%20id.resp_h%2C%20id.resp_p%2C%20local_orig%2C%20local_resp%2C%20service%2C%20orig_bytes%2C%20resp_bytes%2C%20qtype_name%2C%20rcode_name%2C%20query%2C%20answers%2C%20filename%2C%20rx_host%2C%20mime_type%2C%20seen_bytes%2C%20md5%2C%20sha256%2C%20method%2C%20status_code%2C%20host%2C%20uri%2C%20user_agent%2C%20client_headers%2C%20server_name%2C%20subject%2C%20issuer%2C%20established%2C%20validation_status%2C%20cert_chain_fps%2C%20alert.signature_id%2C%20alert.signature%5D%29`;
    }
    case 'yara_corelight': {
      return `uid%3D${encode(uid)}%20OR%20(%23path%3Dyara_corelight%20AND%20alert.name%3D%22${encode(
        alertName,
      )}%22)`;
    }
    case 'anomaly': {
      return `uid%3D${encode(uid)}%20OR%20(%23path%3Danomaly%20AND%20alert.name%3D%22${encode(
        alertName,
      )}%22)`;
    }
    case 'notice': {
      return `uid%3D${encode(uid)}%20OR%20(%23path%3D${encode(
        alertType,
      )}%20AND%20(id.orig_h%3D${encode(entityName)}%20or%20id.resp_h%3D${encode(
        entityName,
      )}%20or%20src%3D${encode(entityName)})%20AND%20note%20%3D%20%22${encode(
        alertName,
      )}%22%29%0A%7C%20select%28%5B%23path%2C%20id.orig_h%2C%20id.resp_h%2C%20id.resp_p%2C%20local_orig%2C%20local_resp%2C%20service%2C%20orig_bytes%2C%20resp_bytes%2C%20note%2C%20msg%2C%20qtype_name%2C%20rcode_name%2C%20query%2C%20answers%2C%20filename%2C%20rx_host%2C%20mime_type%2C%20seen_bytes%2C%20md5%2C%20sha256%2C%20method%2C%20status_code%2C%20host%2C%20uri%2C%20user_agent%2C%20client_headers%2C%20server_name%2C%20subject%2C%20issuer%2C%20established%2C%20validation_status%2C%20cert_chain_fps%5D%29`;
    }
    default: {
      // fallback to uid-only
      return `uid%3D${encode(uid)}`;
    }
  }
}
